import styled, { css } from 'styled-components';
import Select, { StylesConfig } from 'react-select';

import theme from '@styles/theme';
import arrowDownColumn from '@assets/icons/arrowDownColumn.svg';
import { CustomSelectOptionType } from '@utils/types';

import { Description } from '@components/styled/StyledComponents';

type StyledCustomSelectProps = {
    COLOR?: boolean;
    name?: string;
    title?: string;
    description?: string;
    options: CustomSelectOptionType[];
    customOption?: any;
    placeholder?: string;
    value?: CustomSelectOptionType | null;
    defaultValue?: CustomSelectOptionType | null;
    disabled?: boolean;
    onChange: (e: any) => void;
    width?: string;
    margin?: string;
    border?: string;
    borderRadius?: number;
    error?: boolean;
};
const StyledCustomSelect = ({
    COLOR,
    name,
    title,
    description,
    options,
    customOption,
    placeholder,
    value,
    defaultValue,
    disabled,
    onChange,
    width,
    margin = '0',
    border = `${theme.colors.lightGrayBorder} 1px solid`,
    borderRadius = 4,
    error = false
}: StyledCustomSelectProps) => {
    const dot = (color = 'transparent') => ({
        alignItems: 'center',
        display: 'flex',

        ...(typeof value!.value === 'string' && {
            ':before': {
                backgroundColor: color,
                content: '" "',
                display: 'block',
                marginRight: 8,
                height: 16,
                width: 16
            }
        })
    });
    const colourStyles: StylesConfig<CustomSelectOptionType, false> = {
        control: (styles) => ({ ...styles, backgroundColor: 'white' }),
        option: (styles, { isDisabled }) => {
            return {
                ...styles,
                cursor: isDisabled ? 'not-allowed' : 'default'
            };
        },
        input: (styles) => ({ ...styles, ...dot() }),
        placeholder: (styles) => ({ ...styles, ...dot('#ccc') }),
        singleValue: (styles, { data }) => ({
            ...styles,
            ...dot(data.value as string)
        })
    };
    return (
        <Wrapper $margin={margin}>
            {title && <Title>{title}</Title>}
            <CustomSelect
                name={name}
                value={value}
                defaultValue={defaultValue}
                onChange={onChange}
                options={options}
                components={customOption ? { Option: customOption } : undefined}
                placeholder={placeholder}
                isDisabled={disabled}
                styles={COLOR ? colourStyles : undefined}
                $width={width}
                $border={border}
                $borderRadius={borderRadius}
                $error={error}
            />
            {description && <Description>{description}</Description>}
        </Wrapper>
    );
};

export default StyledCustomSelect;

const Wrapper = styled.div<{ $margin?: string }>`
    margin: ${({ $margin }) => $margin};
`;

const Title = styled.div`
    margin: 0 0 8px;
    font-size: 14px;
`;
const CustomSelect = styled(Select<CustomSelectOptionType>)<{
    $width?: string;
    $border?: string;
    $borderRadius?: number;
    $error?: boolean;
}>`
    .css-13cymwt-control,
    .css-t3ipsp-control,
    .css-16xfy0z-control,
    .css-v68sna-control,
    .css-blhudp-control {
        width: ${({ $width }) => ($width ? $width : 'auro')};
        height: 50px;
        min-height: 50px;
        border: ${({ $border }) => $border};
        border-radius: ${({ $borderRadius }) => $borderRadius}px;
        font-size: 14px;
        box-shadow: none;
        background-position: bottom 50% right 12px;
        background-image: url(${arrowDownColumn});
        background-size: 16px;
        background-repeat: no-repeat;
        &:hover {
            border: ${({ $border }) => $border};
        }
    }
    .css-16xfy0z-control {
        .css-olqui2-singleValue {
            color: ${theme.colors.blackColor};
        }
        background-color: ${theme.colors.ultraLightGrayBorder};
    }
    .css-1jqq78o-placeholder {
        color: ${theme.colors.deepGrayFontColor} !important;
    }
    .css-hlgwow {
        padding: 2px 10px;
        > div {
            color: ${theme.colors.blackColor};
            font-size: 16px;
        }
    }
    .css-1u9des2-indicatorSeparator,
    .css-1xc3v61-indicatorContainer,
    .css-15lsz6c-indicatorContainer {
        display: none;
    }
    .css-1nmdiq5-menu {
        top: calc(100% - 8px);
        z-index: 100;
    }
    .css-d7l1ni-option {
        background-color: ${theme.colors.ultraLightGrayBorder};
    }
    ${({ $error }) =>
        $error &&
        css`
            .css-t3ipsp-control {
                border: red 1px solid;
                &:hover {
                    border: red 1px solid;
                }
            }
        `}
`;
